import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, FileText, File, Download, Image } from "lucide-react";
import { Template } from "@/data/templates";

interface TemplateFormProps {
  isEdit?: boolean;
  formData: {
    title: string;
    description: string;
    category: "financial" | "operations" | "development" | "";
    fileType: "Excel" | "PowerPoint" | "Word/PDF" | "";
    tags: string;
  };
  setFormData: React.Dispatch<
    React.SetStateAction<TemplateFormProps["formData"]>
  >;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  formLoading: boolean;
  selectedTemplate: Template | null;
  handleAdd: () => Promise<void>;
  saveEdit: () => Promise<void>;
  setAddDialogOpen: (open: boolean) => void;
  setEditDialogOpen: (open: boolean) => void;
  resetForm: () => void;
}
// Функция для получения иконки по типу файла
const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "xlsx":
    case "xls":
      return <FileText className="h-8 w-8 text-green-600" />;
    case "ppt":
    case "pptx":
      return <FileText className="h-8 w-8 text-orange-600" />;
    case "doc":
    case "docx":
      return <FileText className="h-8 w-8 text-blue-600" />;
    case "pdf":
      return <FileText className="h-8 w-8 text-red-600" />;
    default:
      return <File className="h-8 w-8 text-gray-600" />;
  }
};

const getFileColor = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "xlsx":
    case "xls":
      return "bg-green-50 border-green-200 text-green-700";
    case "ppt":
    case "pptx":
      return "bg-orange-50 border-orange-200 text-orange-700";
    case "doc":
    case "docx":
      return "bg-blue-50 border-blue-200 text-blue-700";
    case "pdf":
      return "bg-red-50 border-red-200 text-red-700";
    default:
      return "bg-gray-50 border-gray-200 text-gray-700";
  }
};

const TemplateForm: React.FC<TemplateFormProps> = React.memo(
  ({
    isEdit = false,
    formData,
    setFormData,
    selectedFile,
    setSelectedFile,
    formLoading,
    selectedTemplate,
    handleAdd,
    saveEdit,
    setAddDialogOpen,
    setEditDialogOpen,
    resetForm,
  }) => {
    const handleCancel = () => {
      if (isEdit) {
        setEditDialogOpen(false);
      } else {
        setAddDialogOpen(false);
      }
      resetForm();
    };

    const handleDownload = () => {
      if (selectedTemplate?.file_url) {
        window.open(selectedTemplate.file_url, "_blank");
      }
    };

    return (
      <div className="space-y-4 pt-4">
        <div className="space-y-2">
          <Label>
            Title <span className="text-destructive">*</span>
          </Label>
          <Input
            placeholder="e.g., Revenue Forecast Model"
            value={formData.title}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, title: e.target.value }))
            }
            disabled={formLoading}
          />
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            placeholder="Describe what this template is for..."
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            disabled={formLoading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>
              Category <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  category: value as "financial" | "operations" | "development",
                }))
              }
            >
              <SelectTrigger disabled={formLoading} className="capitalize">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="financial">Financial</SelectItem>
                <SelectItem value="operations">Operations</SelectItem>
                <SelectItem value="development">
                  Development Templates
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>
              File Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.fileType}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  fileType: value as "Excel" | "PowerPoint" | "Word/PDF",
                }))
              }
            >
              <SelectTrigger disabled={formLoading}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Excel">Excel</SelectItem>
                <SelectItem value="PowerPoint">PowerPoint</SelectItem>
                <SelectItem value="Word/PDF">Word/PDF</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Tags (comma separated)</Label>
          <Input
            placeholder="e.g., Revenue, Forecast, Planning"
            value={formData.tags}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, tags: e.target.value }))
            }
            disabled={formLoading}
          />
        </div>

        {isEdit && selectedTemplate && (
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
            <Label className="font-medium">Current File</Label>
            <div
              className={`flex items-center gap-3 p-3 rounded border ${getFileColor(selectedTemplate.file_name)}`}
            >
              {getFileIcon(selectedTemplate.file_name)}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {selectedTemplate.file_name}
                </p>
                <p className="text-xs opacity-75">
                  Uploaded:{" "}
                  {new Date(selectedTemplate.created_at).toLocaleDateString()}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="shrink-0"
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Upload a new file below to replace the current one
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>
            {isEdit ? "New File (optional)" : "File Upload"}
            {!isEdit && <span className="text-destructive"> *</span>}
          </Label>
          <Input
            type="file"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            disabled={formLoading}
            accept=".xlsx,.xls,.ppt,.pptx,.doc,.docx,.pdf"
          />

          {selectedFile && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <File className="h-6 w-6 text-blue-600" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-blue-600">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={formLoading}
          >
            Cancel
          </Button>
          <Button
            className="bg-primary-blue"
            onClick={isEdit ? saveEdit : handleAdd}
            disabled={formLoading}
          >
            {formLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEdit ? "Saving..." : "Creating..."}
              </>
            ) : (
              <>{isEdit ? "Save Changes" : "Create Template"}</>
            )}
          </Button>
        </DialogFooter>
      </div>
    );
  }
);

export { TemplateForm };
